import { getImageUrl, getVendorBusinessImageUrl } from "./mediaUrl";

/**
 * Standardized Vendor Card Generator
 * @param {Object} v Vendor data object
 * @param {Object} options Configuration options
 * @returns {String} HTML string
 */
export const buildVendorCard = (v, options = {}) => {
  const baseURL = globalThis.baseURL || "/";
  const img = getVendorBusinessImageUrl(v.Image_URL, 1);
  const vendorLink = v.Vendor_URL
    ? `${baseURL}customer/productListing/${v.Vendor_URL}`
    : "#";
  
  const rating = parseFloat(v.Vendor_Rating);
  const hasRating = !isNaN(rating) && rating > 0;
  const ratingLabel = hasRating ? rating : "New";
  const ratingClass = hasRating ? "is-rated" : "is-new";

  const favBtn = options.favBtn || ""; // Passed from the caller (e.g. HomePage handles fav state)
  const itemClass = options.className || (options.isCarousel ? "px-2 pb-3 pt-2" : "col-6 col-md-4 col-lg-3 mb-4");

  return `
    <div class="item ${itemClass}">
      <div class="card near-you-vendor-card-modern h-100 ${vendorLink !== "#" ? "cursor-pointer" : ""}" ${
        vendorLink !== "#"
          ? `data-href="${vendorLink}" onclick="if (!event.target.closest('a, button, input, select, textarea, label')) { window.location.href = this.dataset.href; }"`
          : ""
      }>
        <div class="vendor-card-media-wrap position-relative">
          <a href="${vendorLink}" class="d-block overflow-hidden h-100" ${
            vendorLink === "#" ? 'onclick="return false;"' : ""
          }>
            <img src="${img}" class="vendor-card-img w-100" loading="lazy" onerror="this.src='${baseURL}assets/default.png';this.style.objectFit='contain';" />
            <div class="vendor-card-overlay"></div>
          </a>
          
          <div class="vendor-card-fav">
            ${favBtn}
          </div>
        </div>

        <div class="vendor-card-body p-3">
          <div class="d-flex justify-content-between align-items-start mb-1">
            <h6 class="vendor-card-name text-truncate mb-0 pr-2">${v.Business_Name || v.Name}</h6>
            <div class="vendor-card-rating-pill ${ratingClass} flex-shrink-0">
               ${ratingLabel} <i class="fa fa-star ml-1" style="font-size: 0.65rem; color: #fff;"></i>
            </div>
          </div>
          
          <p class="vendor-card-location mb-3 text-muted small text-truncate">
            ${v.City || (v.Address && v.Address.City) || ""}
          </p>
          
          <div class="vendor-card-footer d-flex justify-content-between align-items-center pt-3 border-top-dashed">
            <span class="vendor-card-min-order">MIN ORDER: ₹${v.Minimum_Order || "0"}</span>
            <span class="vendor-card-delivery-status"><i class="fa-solid fa-truck-fast mr-1"></i> Delivery</span>
          </div>
        </div>
      </div>
    </div>
  `;
};

/**
 * Standardized Product Card Generator
 * @param {Object} product Product data object
 * @param {Object} options Configuration options
 * @returns {String} HTML string
 */
export const buildProductCard = (product, options = {}) => {
  const baseURL = globalThis.baseURL || "/";
  const img = getImageUrl(product.Image_URL);
  const productLink = product.product_URL
    ? `${baseURL}customer/productSummary/${product.product_URL}`
    : "#";

  const sponsoredBadge =
    product.isSponsored > 0
      ? `<span class="position-absolute top-2 start-2 badge text-white" style="background: #ff385c; font-size: 0.75rem; border-radius: 4px; z-index: 6;">Sponsored</span>`
      : "";

  // CATEGORY BADGE LOGIC
  const flow = (product.Picklist_flow || product.Vendor_Flow || "").toLowerCase();
  const subType = product.Picklist_sub_type;

  let badgeClass = "";
  let badgeLabel = product.Category_Name || "Product";

  if (subType === "Service") {
    badgeClass = "is-service-badge";
  } else if (flow === "menu" || product.is_food == 1) {
    badgeClass = "is-food-badge";
  } else {
    badgeClass = "is-shop-badge";
  }

  const categoryBadge = `<span class="top-product-category-badge ${badgeClass}">${badgeLabel}</span>`;
  
  const isVeg = product.Veg_NonVeg_flag === "Veg";
  const isNonVeg = product.Veg_NonVeg_flag === "Non-Veg";
  const vegLabel = (isVeg || isNonVeg)
    ? `<span class="badge bg-light ${isNonVeg ? "text-danger border-danger-subtle" : "text-success border-success-subtle"} border ms-2" style="font-size: 0.65rem;">${product.Veg_NonVeg_flag}</span>`
    : "";

  const productId = String(product.Product_ID || "").trim();
  
  // Resolve Vendor ID
  const candidates = [
    product.Vendor_ID,
    product.Vendor_Id,
    product.VendorID,
    product.vendor_id,
    product.vendorId,
    product.Business_ID,
    product.Business_Id,
    product.business_id,
  ];
  let vendorId = null;
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value) {
      vendorId = value;
      break;
    }
  }

  const quantityValue = Number.parseInt(
    product.Quantity ??
      product.Product_Quantity ??
      product.Available_Quantity ??
      product.Stock ??
      product.stock,
    10,
  );
  const isOutOfStock = Number.isInteger(quantityValue) && quantityValue <= 0;
  const isCartEligible = !!productId && !!vendorId;

  const rating = parseFloat(product.Product_Review_Score || product.Rating);
  const hasRating = !isNaN(rating) && rating > 0;
  const ratingLabel = hasRating ? rating : "0";
  const ratingClass = hasRating ? "is-rated" : "is-new";

  const itemClass = options.className || (options.isCarousel ? "px-1 pb-2 pt-1" : "col-6 col-md-4 col-lg-3 mb-4");

  return `
    <div class="item ${itemClass}">
      <div class="card product-card-modern h-100 ${productLink !== "#" ? "cursor-pointer" : ""}" ${
        productLink !== "#"
          ? `data-href="${productLink}" onclick="if (!event.target.closest('a, button, input, select, textarea, label')) { window.location.href = this.dataset.href; }"`
          : ""
      }>
        <div class="product-card-media-wrap position-relative">
          <a href="${productLink}" class="d-block overflow-hidden h-100" ${
            productLink === "#" ? 'onclick="return false;"' : ""
          }>
            <img src="${img}" class="product-card-img w-100 ${product.Product_ID}-img" loading="lazy" onerror="this.src='${baseURL}assets/default.png';this.style.objectFit='contain';" />
            <div class="product-card-overlay"></div>
          </a>
          ${sponsoredBadge}
          <div class="product-card-badge-wrap">
             ${categoryBadge}
          </div>
        </div>

        <div class="product-card-body p-3">
          <div class="d-flex justify-content-between align-items-start mb-1">
            <h6 class="product-card-name text-truncate mb-0 pr-2">${product.Product_Service_Name}${vegLabel}</h6>
            <div class="product-card-rating-pill ${ratingClass} flex-shrink-0">
               ${ratingLabel} <i class="fa fa-star ml-1" style="font-size: 0.65rem; color: #fff;"></i>
            </div>
          </div>
          
          <p class="product-card-vendor-info text-truncate mb-3 text-muted small">
            ${product.Business_Name || product.Vendor_Name || ""}${product.City ? `, ${product.City}` : ""}
          </p>
          
          <div class="product-card-footer d-flex justify-content-between align-items-center pt-3 border-top-dashed">
            <span class="product-card-price-main">₹${product.Price}</span>
            <div class="product-add-btn">
               ${
                 isCartEligible && !isOutOfStock
                   ? `<button type="button" class="btn btn-sm text-white px-3 addToCartBtn" style="background: #ff385c; border-radius: 8px; font-weight: 600;" value="${productId}" data-vendor="${vendorId || ""}">ADD</button>`
                   : isOutOfStock
                     ? `<button type="button" class="btn btn-sm btn-secondary text-white px-3" style="border-radius: 8px;" disabled>Out of stock</button>`
                     : `<button type="button" class="btn btn-sm btn-secondary text-white px-3" style="border-radius: 8px;" disabled>Not available</button>`
               }
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};
